import { ref, computed, watch } from 'vue'
import { useAuthStore } from '@/stores/auth.store'
import { useRouter } from 'vue-router'
import api from '@/services/api'

export function useForcePasswordChange() {
  const authStore = useAuthStore()
  const router = useRouter()
  
  const showModal = ref(false)
  const passwordExpiresAt = ref<string | null>(null)
  const loading = ref(false)

  // Vérifier si un changement de mot de passe est requis
  const requiresPasswordChange = computed(() => {
    return authStore.user?.forcePasswordChange || false
  })

  // Vérifier si c'est la première connexion
  const isFirstLogin = computed(() => {
    return authStore.user?.firstLogin || false
  })

  // Vérifier si le mot de passe a expiré
  const isPasswordExpired = computed(() => {
    if (!authStore.user?.passwordExpiresAt) return false
    return new Date() > new Date(authStore.user.passwordExpiresAt)
  })

  // Vérifier si le modal doit être affiché
  // Le modal doit s'afficher SEULEMENT si l'utilisateur doit changer son mot de passe de manière forcée
  const shouldShowModal = computed(() => {
    // Vérifier si c'est un changement forcé
    const isForcedChange = requiresPasswordChange.value || isPasswordExpired.value || isFirstLogin.value
    
    // Le modal ne s'affiche que si c'est un changement forcé ET que showModal est true
    if (!isForcedChange) {
      // Si ce n'est pas un changement forcé, ne jamais afficher le modal
      return false
    }
    
    // Si c'est un changement forcé, afficher le modal (showModal sera mis à true par les watchers)
    return showModal.value
  })

  // Initialiser la vérification après la connexion
  const checkPasswordChangeRequired = async () => {
    if (!authStore.user) return

    try {
      loading.value = true
      
      // Vérifier le statut via l'API Axios configurée
      const { data } = await api.get('/auth/password-change-required')
      const status = data?.data ?? data
      
      // Mettre à jour passwordExpiresAt si disponible
      if (status?.passwordExpiresAt) {
        passwordExpiresAt.value = status.passwordExpiresAt
      }
      
      // Le modal s'affichera automatiquement via shouldShowModal si nécessaire
      // On met showModal à true pour permettre la fermeture manuelle si nécessaire
      if (status?.requiresPasswordChange || isFirstLogin.value || isPasswordExpired.value) {
        showModal.value = true
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du changement de mot de passe:', error)
      // Même en cas d'erreur, vérifier les propriétés de l'utilisateur directement
      if (requiresPasswordChange.value || isFirstLogin.value || isPasswordExpired.value) {
        showModal.value = true
      }
    } finally {
      loading.value = false
    }
  }

  // Gérer la fermeture du modal
  // Le modal ne peut être fermé que si ce n'est pas un changement forcé
  const closeModal = () => {
    // Si c'est un changement forcé, ne pas permettre la fermeture
    if (requiresPasswordChange.value || isPasswordExpired.value || isFirstLogin.value) {
      return
    }
    
    showModal.value = false
    passwordExpiresAt.value = null
  }

  // Gérer le succès du changement de mot de passe
  const handlePasswordChangeSuccess = async () => {
    try {
      // Fermer le modal
      closeModal()
      
      // Déconnecter l'utilisateur après le changement de mot de passe
      await authStore.logout()
      
      // Rediriger vers la page de connexion avec un message
      await router.push({
        path: '/login',
        query: {
          message: 'Votre mot de passe a été modifié avec succès. Veuillez vous reconnecter avec votre nouveau mot de passe.'
        }
      })
    } catch (error) {
      console.error('Erreur lors de la déconnexion après changement de mot de passe:', error)
      // En cas d'erreur, rediriger quand même vers la page de connexion
      await router.push({
        path: '/login',
        query: {
          message: 'Votre mot de passe a été modifié avec succès. Veuillez vous reconnecter.'
        }
      })
    }
  }

  // Vérifier automatiquement après la connexion
  const initializePasswordCheck = () => {
    if (authStore.isAuthenticated && authStore.user) {
      checkPasswordChangeRequired()
    }
  }

  // Surveiller les changements de l'utilisateur pour afficher automatiquement le modal
  // SEULEMENT si c'est un changement forcé
  watch(
    () => authStore.user,
    (newUser) => {
      if (newUser && authStore.isAuthenticated) {
        // Vérifier si c'est un changement forcé
        const isForced = newUser.forcePasswordChange || newUser.firstLogin || isPasswordExpired.value
        
        if (isForced) {
          // Si l'utilisateur doit changer son mot de passe de manière forcée, afficher le modal automatiquement
          showModal.value = true
          // Vérifier aussi via l'API pour obtenir les informations complètes
          checkPasswordChangeRequired()
        } else {
          // Si ce n'est pas un changement forcé, ne pas afficher le modal
          showModal.value = false
        }
      } else {
        // Si l'utilisateur se déconnecte, fermer le modal
        showModal.value = false
      }
    },
    { immediate: true, deep: true }
  )

  // Surveiller aussi les computed pour réagir aux changements
  // SEULEMENT pour les changements forcés
  watch(
    [requiresPasswordChange, isFirstLogin, isPasswordExpired],
    ([requiresChange, firstLogin, expired]) => {
      if (authStore.isAuthenticated && authStore.user) {
        const isForced = requiresChange || firstLogin || expired
        
        if (isForced) {
          // Afficher le modal seulement si c'est un changement forcé
          showModal.value = true
        } else {
          // Ne pas afficher le modal si ce n'est pas un changement forcé
          showModal.value = false
        }
      }
    },
    { immediate: true }
  )

  return {
    showModal,
    passwordExpiresAt,
    loading,
    requiresPasswordChange,
    isFirstLogin,
    isPasswordExpired,
    shouldShowModal,
    checkPasswordChangeRequired,
    closeModal,
    handlePasswordChangeSuccess,
    initializePasswordCheck
  }
}
